import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Badge, Modal, EmptyState, LoadingSkeleton, FilterBar, FilterSelect, FilterSearch } from './ui';
import { EnvelopeIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';
import useApi from './portal/useApi';
import { formatMoney, formatDate } from './portal/format';
import portal from './portal/Portal.module.css';
import styles from './OutstandingFees.module.css';

const NETWORK_ERROR = 'Network error. Please check your connection and try again.';
const STATUS_BADGE = { sent: 'success', failed: 'danger', skipped: 'warning', queued: 'info' };
const DAY_MS = 24 * 60 * 60 * 1000;

function daysSince(iso) {
  return iso ? Math.floor((Date.now() - new Date(`${iso}Z`).getTime()) / DAY_MS) : null;
}

// Who owes fees, and payment reminders to their parents — one student at a
// time or in bulk. Administrator / principal only (enforced by the backend).
function OutstandingFees({ token }) {
  const outstanding = useApi(token, '/api/v1/fees/outstanding');
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [minBalance, setMinBalance] = useState('');
  const [reminded, setReminded] = useState('');
  const [selected, setSelected] = useState([]);
  const [confirming, setConfirming] = useState(null); // rows about to be reminded
  const [note, setNote] = useState('');
  const [force, setForce] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // response of the last remind request
  const [batchId, setBatchId] = useState(null);
  const [poll, setPoll] = useState(0);

  const batch = useApi(token, batchId ? `/api/v1/fees/outstanding/reminders?batch_id=${batchId}&poll=${poll}` : null);
  const batchDone = Boolean(batch.data?.done);

  // Poll the running batch every 3s; refresh the table once it finishes.
  useEffect(() => {
    if (!batchId || batch.loading) return undefined;
    if (batchDone) {
      outstanding.reload();
      return undefined;
    }
    const timer = setTimeout(() => setPoll((count) => count + 1), 3000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, batch.loading, batchDone, poll]);

  const students = useMemo(() => outstanding.data?.students || [], [outstanding.data]);
  const summary = outstanding.data?.summary;
  const emailReady = outstanding.data?.email_configured;

  const classOptions = [...new Set(students.map((row) => row.class_name).filter(Boolean))].sort();
  const term = search.trim().toLowerCase();
  const rows = students
    .filter((row) => !term || row.student_name.toLowerCase().includes(term))
    .filter((row) => !classFilter || row.class_name === classFilter)
    .filter((row) => !minBalance || row.balance_usd >= Number(minBalance))
    .filter((row) => {
      if (!reminded) return true;
      const days = daysSince(row.last_reminded_at);
      return reminded === 'never' ? days === null : (days === null || days >= 7);
    });
  const filtersActive = Boolean(term || classFilter || minBalance || reminded);
  const clearFilters = () => { setSearch(''); setClassFilter(''); setMinBalance(''); setReminded(''); };

  const selectable = rows.filter((row) => row.contacts.length > 0);
  const selectedRows = students.filter((row) => selected.includes(row.student_id));
  const allSelected = selectable.length > 0 && selectable.every((row) => selected.includes(row.student_id));
  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  const toggleAll = () => setSelected(allSelected ? [] : selectable.map((row) => row.student_id));

  const openConfirm = (targets) => { setConfirming(targets); setNote(''); setForce(false); setError(''); };

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      const response = await fetch('/api/v1/fees/outstanding/remind', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ student_ids: confirming.map((row) => row.student_id), message: note, force }),
      });
      if (response.ok) {
        const data = await response.json();
        setResult(data);
        setBatchId(data.batch_id);
        setPoll(0);
        setSelected([]);
        setConfirming(null);
      } else {
        setError(await safeErrorMessage(response, 'Failed to send reminders'));
      }
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setSending(false);
    }
  };

  const columns = [
    {
      header: <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all students shown" disabled={selectable.length === 0} />,
      width: '2.5rem',
      render: (row) => (
        <input type="checkbox" checked={selected.includes(row.student_id)} onChange={() => toggle(row.student_id)} disabled={row.contacts.length === 0} aria-label={`Select ${row.student_name}`} />
      ),
    },
    { header: 'Student', render: (row) => (<div><strong>{row.student_name}</strong><div className={styles.sub}>{row.class_name || 'No class'}</div></div>) },
    { header: 'Charged', render: (row) => formatMoney(row.total_fees_usd) },
    { header: 'Paid', render: (row) => formatMoney(row.total_paid_usd) },
    { header: 'Outstanding', render: (row) => (<span className={styles.balance}>{formatMoney(row.balance_usd)}{row.is_overdue && <Badge variant="danger" className={styles.overdue}>Overdue</Badge>}</span>) },
    {
      header: 'Reminder goes to',
      render: (row) => (row.contacts.length === 0
        ? <Badge variant="warning">No contact</Badge>
        : <div className={styles.sub}>{row.contacts.map((contact) => contact.email).join(', ')}</div>),
    },
    {
      header: 'Last reminded',
      render: (row) => (row.last_reminded_at
        ? <div>{formatDate(row.last_reminded_at)}<div className={styles.sub}>{row.reminder_count} sent</div></div>
        : <span className={styles.sub}>Never</span>),
    },
    {
      header: '',
      render: (row) => (
        <Button variant="secondary" size="sm" onClick={() => openConfirm([row])} disabled={row.contacts.length === 0 || !emailReady}>
          <EnvelopeIcon style={{ width: 14, height: 14 }} />
          Remind
        </Button>
      ),
    },
  ];

  const emailCount = (confirming || []).reduce((total, row) => total + row.contacts.length, 0);
  const counts = batch.data?.counts || {};

  return (
    <div>
      <div className={portal.header}>
        <div>
          <h2 className={portal.title}>Outstanding Fees</h2>
          <p className={portal.subtitle}>Students whose fees charged are more than their payments, and reminders to their parents.</p>
        </div>
        <Button variant="primary" size="sm" onClick={() => openConfirm(selectedRows)} disabled={selectedRows.length === 0 || !emailReady}>
          <EnvelopeIcon style={{ width: 16, height: 16 }} />
          Send reminders to selected ({selectedRows.length})
        </Button>
      </div>

      {outstanding.error && <div className="alert alert-error">{outstanding.error}</div>}
      {outstanding.data && !emailReady && (
        <div className="alert alert-warning">Email is not set up for this school, so reminders cannot be sent yet. Ask the Super Admin to configure the school's email sender.</div>
      )}

      {summary && (
        <div className={portal.statsOnPage}>
          <div className={portal.stat}><div className={portal.statLabel}>Students owing</div><div className={portal.statValue}>{summary.students_owing}</div></div>
          <div className={portal.stat}><div className={portal.statLabel}>Total outstanding</div><div className={`${portal.statValue} ${summary.total_outstanding_usd > 0 ? portal.statBad : portal.statGood}`}>{formatMoney(summary.total_outstanding_usd)}</div></div>
          <div className={portal.stat}><div className={portal.statLabel}>No parent contact</div><div className={portal.statValue}>{summary.without_contact}</div></div>
        </div>
      )}

      {result && (
        <Card className={styles.progress}>
          {result.batch_id ? (
            <>
              <strong>{batchDone ? 'Reminders finished' : 'Sending reminders…'}</strong>
              <p className={styles.sub}>
                {result.queued_emails} {result.queued_emails === 1 ? 'email' : 'emails'} for {result.queued_students} {result.queued_students === 1 ? 'student' : 'students'}:
                {' '}{counts.sent || 0} sent, {counts.failed || 0} failed, {counts.skipped || 0} skipped, {counts.queued ?? result.queued_emails} waiting.
                {!batchDone && ' Each email takes a few seconds; you can leave this page.'}
              </p>
              {batchDone && (batch.data.reminders || []).some((item) => item.status !== 'sent') && (
                <Table
                  columns={[
                    { header: 'Student', accessor: 'student_name' },
                    { header: 'To', accessor: 'recipient_email' },
                    { header: 'Result', render: (row) => <Badge variant={STATUS_BADGE[row.status] || 'default'}>{row.status}</Badge> },
                    { header: 'Reason', render: (row) => row.error || '—' },
                  ]}
                  data={batch.data.reminders.filter((item) => item.status !== 'sent')}
                />
              )}
            </>
          ) : (
            <strong>No reminders were sent.</strong>
          )}
          {result.skipped.length > 0 && (
            <>
              <p className={styles.sub}>{result.skipped.length} not reminded:</p>
              <Table
                columns={[{ header: 'Student', render: (row) => row.student_name || `#${row.student_id}` }, { header: 'Why', accessor: 'reason' }]}
                data={result.skipped}
              />
            </>
          )}
        </Card>
      )}

      <FilterBar shown={rows.length} total={students.length} onClear={filtersActive ? clearFilters : undefined}>
        <FilterSearch value={search} onChange={setSearch} placeholder="Student name" />
        <FilterSelect label="Class" value={classFilter} onChange={setClassFilter} options={classOptions} allLabel="All classes" />
        <FilterSelect label="Owing at least" value={minBalance} onChange={setMinBalance} options={['50', '100', '250', '500', '1000'].map((value) => ({ value, label: `$${value}` }))} allLabel="Any amount" />
        <FilterSelect label="Reminded" value={reminded} onChange={setReminded} options={[{ value: 'week', label: 'Not in the last 7 days' }, { value: 'never', label: 'Never reminded' }]} allLabel="Any" />
      </FilterBar>

      <Card>
        {outstanding.loading && !outstanding.data ? (
          <LoadingSkeleton type="text" rows={8} />
        ) : students.length === 0 ? (
          <EmptyState title="No outstanding fees" message="Every student's fees are fully paid." icon={<BanknotesIcon />} />
        ) : (
          <Table columns={columns} data={rows} emptyMessage="No students match these filters." />
        )}
      </Card>

      <Modal isOpen={Boolean(confirming)} onClose={() => setConfirming(null)} title="Send payment reminders" width="520px">
        {confirming && (
          <div>
            {error && <div className="alert alert-error">{error}</div>}
            <p className={styles.confirmText}>
              This emails <strong>{emailCount} {emailCount === 1 ? 'parent' : 'parents'}</strong> about{' '}
              <strong>{confirming.length === 1 ? confirming[0].student_name : `${confirming.length} students`}</strong>, with the outstanding balance for their own child.
            </p>
            <div className="form-group">
              <label htmlFor="reminder-note">Add a note to the email (optional)</label>
              <textarea id="reminder-note" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. The accounts office is open 8am to 4pm on weekdays." />
            </div>
            <label className={styles.checkLine}>
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
              Also send to parents who were already reminded in the last 24 hours
            </label>
            <div className={styles.actions}>
              <Button variant="primary" onClick={handleSend} disabled={sending || emailCount === 0}>{sending ? 'Queuing…' : `Send ${emailCount} ${emailCount === 1 ? 'email' : 'emails'}`}</Button>
              <Button variant="secondary" onClick={() => setConfirming(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default OutstandingFees;
